import { forwardRef, type ButtonHTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-xs font-bold tracking-widest uppercase transition-all duration-150 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-40 active:scale-95',
  {
    variants: {
      variant: {
        default:  'bg-accent text-white hover:opacity-85',
        ghost:    'border border-border2 text-muted hover:text-ink hover:border-muted bg-transparent',
        danger:   'bg-red/10 text-red border border-red/30 hover:bg-red/20',
        green:    'bg-green text-bg hover:opacity-85',
        subtle:   'bg-surface text-muted hover:text-ink',
      },
      size: {
        default: 'h-8 px-4',
        sm:      'h-7 px-3 text-[10px]',
        lg:      'h-10 px-6',
        icon:    'h-8 w-8 p-0',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />
  )
)
Button.displayName = 'Button'

export { Button, buttonVariants }
